/**
 * Platform Admin API client + TanStack Query hooks.
 *
 * Backed by /api/v1/admin/* — every endpoint requires a row in
 * `platform_staff` server-side, so even if a non-admin user knew the URL
 * they'd be 403'd. The frontend gates the routes with <AdminGuard /> which
 * calls /api/v1/admin/whoami (cheap GET that 200s for admins, 403s otherwise).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api'

// ────────────────────────────── orgs ────────────────────────────────────────

export type OrgStatus = 'active' | 'suspended' | 'trial'

export interface AdminOrg {
  id: string
  name: string
  slug: string
  billing_email: string
  status: OrgStatus
  stripe_customer_id: string | null
  created_at: string
}

export interface PaginatedResponse<T> {
  items: readonly T[]
  page: number
  page_size: number
  total: number
}

export interface AdminOrgFilters {
  page?: number
  page_size?: number
  status?: OrgStatus
  q?: string
}

const adminKeys = {
  whoami: ['admin', 'whoami'] as const,
  orgs: (f: AdminOrgFilters) => ['admin', 'orgs', f] as const,
  failedJobs: (f: FailedJobFilters) => ['admin', 'failed-jobs', f] as const,
  flags: ['admin', 'feature-flags'] as const,
  audit: (f: AuditFilters) => ['admin', 'audit-log', f] as const,
  clientErrors: (f: ClientErrorFilters) => ['admin', 'client-errors', f] as const,
}

export const adminQueryKeys = adminKeys

function qs(params: object): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const out = sp.toString()
  return out ? `?${out}` : ''
}

/**
 * Cheap probe that just exercises the requirePlatformAdmin guard. The
 * <AdminGuard /> component reads it; if the API 403s we redirect to /.
 *
 * Implementation note: we reuse the orgs list endpoint with page_size=1 so
 * we don't have to add a dedicated route. 403 → not admin; 200 → admin.
 */
export interface AdminWhoami {
  is_admin: boolean
}

export async function fetchAdminWhoami(): Promise<AdminWhoami> {
  try {
    await api<PaginatedResponse<AdminOrg>>('/api/v1/admin/orgs?page_size=1')
    return { is_admin: true }
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return { is_admin: false }
    }
    throw err
  }
}

export function useAdminWhoami() {
  return useQuery({
    queryKey: adminKeys.whoami,
    queryFn: fetchAdminWhoami,
    // Once we know, we know — no need to refetch on focus.
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  })
}

export function useAdminOrgs(filters: AdminOrgFilters = {}) {
  return useQuery({
    queryKey: adminKeys.orgs(filters),
    queryFn: () => api<PaginatedResponse<AdminOrg>>(`/api/v1/admin/orgs${qs(filters)}`),
  })
}

export function useUpdateAdminOrg() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrgStatus }) =>
      api<{ id: string; status: OrgStatus }>(`/api/v1/admin/orgs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'orgs'] })
    },
  })
}

// ───────────────────────── failed-jobs ──────────────────────────────────────

export interface AdminFailedJob {
  id: string
  organization_id: string
  job_name: string
  input_hash: string
  attempt_count: number
  error_message: string | null
  last_attempt_at: string
  resolved_at: string | null
}

export interface FailedJobFilters {
  page?: number
  page_size?: number
  job_name?: string
  unresolved?: boolean
}

export function useFailedJobs(filters: FailedJobFilters = {}) {
  return useQuery({
    queryKey: adminKeys.failedJobs(filters),
    queryFn: () =>
      api<PaginatedResponse<AdminFailedJob>>(`/api/v1/admin/failed-jobs${qs(filters)}`),
  })
}

export function useReplayFailedJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; job_id: string; dispatched: boolean }>(
        `/api/v1/admin/failed-jobs/${id}/replay`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'failed-jobs'] })
    },
  })
}

export function useResolveFailedJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/v1/admin/failed-jobs/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'failed-jobs'] })
    },
  })
}

// ───────────────────────── feature-flags ────────────────────────────────────

export interface AdminFeatureFlag {
  key: string
  default_enabled: boolean
  enabled_orgs: readonly string[]
  enabled_percentage: number
  metadata: Record<string, unknown>
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: adminKeys.flags,
    queryFn: () => api<{ items: readonly AdminFeatureFlag[] }>('/api/v1/admin/feature-flags'),
  })
}

export interface CreateFeatureFlagInput {
  key: string
  default_enabled?: boolean
  enabled_orgs?: readonly string[]
  enabled_percentage?: number
  metadata?: Record<string, unknown>
}

export function useCreateFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFeatureFlagInput) =>
      api<AdminFeatureFlag>('/api/v1/admin/feature-flags', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.flags })
    },
  })
}

export interface UpdateFeatureFlagInput {
  default_enabled?: boolean
  enabled_orgs?: readonly string[]
  enabled_percentage?: number
  metadata?: Record<string, unknown>
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: UpdateFeatureFlagInput }) =>
      api<AdminFeatureFlag>(`/api/v1/admin/feature-flags/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.flags })
    },
  })
}

export function useDeleteFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (key: string) =>
      api<{ ok: true }>(`/api/v1/admin/feature-flags/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.flags })
    },
  })
}

// ───────────────────────── audit-log ────────────────────────────────────────

export interface AdminAuditLogRow {
  id: string
  organization_id: string
  user_id: string | null
  action: string
  target_type: string
  target_id: string | null
  metadata: Record<string, unknown>
  ip: string | null
  timestamp: string
}

export interface AuditFilters {
  page?: number
  page_size?: number
  organization_id?: string
  user_id?: string
  action?: string
  since?: string
  until?: string
}

export function useAuditLog(filters: AuditFilters = {}) {
  return useQuery({
    queryKey: adminKeys.audit(filters),
    queryFn: () =>
      api<PaginatedResponse<AdminAuditLogRow>>(`/api/v1/admin/audit-log${qs(filters)}`),
  })
}

// ────────────────────────── client-errors ────────────────────────────────────

export interface AdminClientError {
  id: string
  org_id: string | null
  user_id: string | null
  session_id: string | null
  released_version: string | null
  route: string | null
  error_message: string
  error_stack: string | null
  user_agent: string | null
  viewport_w: number | null
  viewport_h: number | null
  first_seen_at: string
  created_at: string
  resolved_at: string | null
  resolved_by: string | null
}

export interface ClientErrorFilters {
  page?: number
  page_size?: number
  released_version?: string
  route?: string
  unresolved?: boolean
  since?: string
  until?: string
}

export function useClientErrors(filters: ClientErrorFilters = {}) {
  return useQuery({
    queryKey: adminKeys.clientErrors(filters),
    queryFn: () =>
      api<PaginatedResponse<AdminClientError>>(`/api/v1/admin/client-errors${qs(filters)}`),
  })
}

export function useResolveClientError() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/v1/admin/client-errors/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'client-errors'] })
    },
  })
}
